<template>
  <aside class="order-rail">

    <!-- Header -->
    <div class="rail-header">
      <div class="rail-header__top">
        <h3 class="rail-header__title">Resumen del pedido</h3>
        <Pill bg="var(--bg)" color="var(--text-2)">{{ fmtCount(totalLines) }} lineas · {{ fmtCount(totalUnits) }} u</Pill>
      </div>
      <div class="rail-header__total">
        <span class="rail-header__amount">{{ money(grand) }}</span>
        <span class="rail-header__total-label">total estimado</span>
      </div>
    </div>

    <!-- Per-comedor list -->
    <div class="rail-body">
      <div v-if="activeIds.length === 0" class="rail-empty">
        No hay comedores activos.
      </div>

      <div
        v-for="{ cid, lines, total } in perComedor"
        :key="cid"
        class="rail-card"
      >
        <!-- Comedor toggle header -->
        <button
          @click="toggleExpand(cid)"
          class="rail-card__toggle"
          :class="{ 'rail-card__toggle--has-items': lines.length > 0 }"
        >
          <div class="rail-card__toggle-left">
            <div class="rail-card__dot" :class="{ 'rail-card__dot--active': lines.length > 0 }"></div>
            <span class="rail-card__name">{{ getComedor(cid)?.name }}</span>
            <span class="rail-card__count">{{ lines.length }} item{{ lines.length !== 1 ? 's' : '' }}</span>
          </div>
          <div class="rail-card__toggle-right">
            <span class="rail-card__subtotal">{{ money(total) }}</span>
            <div class="rail-card__chevron" :class="{ 'rail-card__chevron--open': isExpanded(cid) }">
              <Icon name="chevD" :size="14" />
            </div>
          </div>
        </button>

        <template v-if="isExpanded(cid)">
          <!-- Line items -->
          <div v-if="lines.length > 0" class="rail-card__lines">
            <div
              v-for="[pid, qty] in lines"
              :key="pid"
              class="rail-line"
            >
              <span class="rail-line__name">{{ getProduct(pid)?.name }}</span>
              <span class="rail-line__qty">x{{ qty }}</span>
              <span class="rail-line__price">{{ money((getProduct(pid)?.price || 0) * qty) }}</span>
            </div>
          </div>

          <!-- Empty state -->
          <div v-else class="rail-card__empty">
            Sin productos todavía.
          </div>
        </template>
      </div>
    </div>

    <!-- Checkout footer -->
    <div class="rail-footer">
      <button
        @click="$emit('checkout')"
        :disabled="grand === 0"
        class="rail-checkout-btn"
        :class="{ 'rail-checkout-btn--disabled': grand === 0 }"
      >
        <Icon name="check" :size="16" color="#fff" /> Confirmar pedido · {{ money(grand) }}
      </button>
    </div>

  </aside>
</template>

<script setup>
import { computed } from 'vue'
import Icon from './ui/Icon.vue'
import Pill from './ui/Pill.vue'

function money(n) { return '$' + n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
function fmtCount(n) { return n.toLocaleString('es-MX') }

const props = defineProps({
  activeIds: { type: Array,  required: true },
  cart:      { type: Object, required: true },
  notes:     { type: Object, required: true },
  expanded:  { type: Object, required: true },
  store:     { type: Object, required: true },
})

const emit = defineEmits(['update:notes', 'update:expanded', 'checkout'])

function getComedor(id) {
  return props.store.getCenterById(id)
}

function getProduct(id) {
  return props.store.getProductById(id)
}

const perComedor = computed(() =>
  props.activeIds.map(cid => {
    const lines = Object.entries(props.cart[cid] || {}).filter(([, q]) => q > 0)
    const total = lines.reduce((s, [pid, q]) => s + (props.store.getProductById(pid)?.price || 0) * q, 0)
    const count = lines.reduce((s, [, q]) => s + q, 0)
    return { cid, lines, total, count }
  })
)

const grand = computed(() => perComedor.value.reduce((s, p) => s + p.total, 0))
const totalLines = computed(() => perComedor.value.reduce((s, p) => s + p.lines.length, 0))
const totalUnits = computed(() => perComedor.value.reduce((s, p) => s + p.count, 0))

function isExpanded(cid) {
  const key = `${cid}-exp`
  return props.expanded[key] !== false
}

function toggleExpand(cid) {
  const key = `${cid}-exp`
  const current = props.expanded[key] !== false
  emit('update:expanded', { ...props.expanded, [key]: !current })
}

function handleNoteInput(cid, event) {
  emit('update:notes', { ...props.notes, [cid]: event.target.value })
}
</script>

<style scoped>
/* ─── Rail shell ─────────────────────────────────────────────────────────────── */
.order-rail {
  width: 360px;
  background: var(--bg);
  border-left: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  height: 100%;
  flex-shrink: 0;
}

/* ─── Header ─────────────────────────────────────────────────────────────────── */
.rail-header {
  padding: 20px 22px 12px;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
  flex-shrink: 0;
}

.rail-header__top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.rail-header__title {
  margin: 0;
  font-family: var(--ff-head);
  font-size: 16px;
  color: var(--text);
  font-weight: 700;
}

.rail-header__total {
  margin-top: 14px;
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.rail-header__amount {
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 28px;
  color: var(--text);
  letter-spacing: -0.5px;
}

.rail-header__total-label {
  font-family: var(--ff-body);
  font-size: 12px;
  color: var(--text-3);
}

/* ─── Body ───────────────────────────────────────────────────────────────────── */
.rail-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
}

.rail-empty {
  padding: 40px;
  text-align: center;
  color: var(--text-3);
  font-family: var(--ff-body);
  font-size: 13px;
}

/* ─── Card ───────────────────────────────────────────────────────────────────── */
.rail-card {
  background: var(--surface);
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 10px;
  border: 1px solid var(--border);
}

.rail-card__toggle {
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0;
}

.rail-card__toggle--has-items {
  margin-bottom: 10px;
}

.rail-card__toggle-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rail-card__dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: var(--border-strong);
}

.rail-card__dot--active {
  background: var(--primary);
}

.rail-card__name {
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 13px;
  color: var(--text);
}

.rail-card__count {
  font-family: var(--ff-body);
  font-size: 11px;
  color: var(--text-3);
}

.rail-card__toggle-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.rail-card__subtotal {
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 13px;
  color: var(--text);
}

.rail-card__chevron {
  transition: transform 0.15s;
  color: var(--text-3);
}

.rail-card__chevron--open {
  transform: rotate(180deg);
}

/* ─── Line items ─────────────────────────────────────────────────────────────── */
.rail-card__lines {
  border-top: 1px dashed var(--border);
  padding-top: 10px;
}

.rail-line {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 4px 0;
  font-family: var(--ff-body);
  font-size: 12px;
  gap: 8px;
}

.rail-line__name {
  color: var(--text-2);
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rail-line__qty {
  color: var(--text-3);
}

.rail-line__price {
  color: var(--text);
  font-weight: 700;
  min-width: 60px;
  text-align: right;
}

.rail-card__empty {
  font-family: var(--ff-body);
  font-size: 11px;
  color: var(--text-3);
  padding: 4px 0;
}

/* ─── Footer ─────────────────────────────────────────────────────────────────── */
.rail-footer {
  padding: 16px;
  background: var(--surface);
  border-top: 1px solid var(--border);
  flex-shrink: 0;
}

.rail-checkout-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 52px;
  padding: 0 22px;
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: var(--ff-head);
  font-weight: 600;
  font-size: 15px;
  transition: opacity 0.15s;
}

.rail-checkout-btn--disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
